import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronLeft, ChevronRight, X, Clock, Calendar, ZoomIn } from 'lucide-react';
import { getBlogPost } from '../services/dataService';
import { BlogPost as BlogPostType } from '../types';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const formatDate = (iso: string) => {
  try { return format(parseISO(iso), 'd MMMM yyyy', { locale: nl }); }
  catch { return iso; }
};

const readingTime = (text: string) => {
  const words = (text ?? '').replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const AuthorAvatar: React.FC<{ name: string }> = ({ name }) => {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="w-12 h-12 rounded-full bg-brand-800 text-white text-base font-bold flex items-center justify-center flex-shrink-0 ring-4 ring-brand-100">
      {initials}
    </div>
  );
};

/* ── Reading progress bar ──────────────────────────────────────── */
const ReadingProgress: React.FC = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-brand-100/40">
      <div
        className="h-full transition-all duration-100 ease-out"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #8e7765 0%, #cabcaf 60%, #a89482 100%)',
        }}
      />
    </div>
  );
};

/* ── Skeleton loading ──────────────────────────────────────────── */
const PostSkeleton: React.FC = () => (
  <div className="min-h-screen bg-brand-50">
    <div className="h-[60vh] skeleton" />
    <div className="max-w-3xl mx-auto px-6 py-14 space-y-4">
      <div className="h-6 skeleton rounded-full w-3/4" />
      <div className="h-4 skeleton rounded-full w-full" />
      <div className="h-4 skeleton rounded-full w-5/6" />
      <div className="h-4 skeleton rounded-full w-4/6" />
    </div>
  </div>
);

/* ── Main component ────────────────────────────────────────────── */
export const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError('');
    getBlogPost(slug)
      .then((p) => { if (!p) setError('Bericht niet gevonden.'); else setPost(p); })
      .catch(() => setError('Kon het bericht niet laden.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const galleryImages = post
    ? [post.cover_image, ...(post.images ?? [])].filter(Boolean) as string[]
    : [];

  const extraImages = post ? (post.images ?? []).filter(Boolean) : [];

  /* ── Lightbox keyboard nav ── */
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') setLightboxIndex(i => Math.max(0, (i ?? 0) - 1));
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(galleryImages.length - 1, (i ?? 0) + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, galleryImages.length]);

  if (loading) return <PostSkeleton />;

  if (error || !post) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-brand-50 px-6">
        <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mb-5">
          <BookOpen size={28} className="text-brand-400" />
        </div>
        <h2 className="font-serif text-2xl font-bold text-brand-900 mb-2">{error || 'Niet gevonden'}</h2>
        <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-brand-600 hover:text-brand-900 font-medium text-sm transition-colors">
          <ArrowLeft size={15} /> Terug naar het blog
        </Link>
      </div>
    );
  }

  const mins = readingTime(post.content ?? '');

  return (
    <div className="min-h-screen bg-brand-50">
      <ReadingProgress />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div className={`relative w-full overflow-hidden ${post.cover_image ? 'h-[60vh] min-h-[420px]' : 'bg-brand-900 py-24 md:py-32'}`}>
        {post.cover_image ? (
          <>
            <img
              src={post.cover_image}
              alt={post.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Multi-layer gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-900/90 via-brand-900/40 to-brand-900/10" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-900/30 to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-700/30 blur-3xl pointer-events-none" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{ backgroundImage: 'radial-gradient(circle, #d4c5b2 1px, transparent 1px)', backgroundSize: '28px 28px' }}
            />
          </>
        )}

        {/* Hero content */}
        <div className={`relative h-full flex flex-col ${post.cover_image ? 'justify-end' : 'justify-center'}`}>
          <div className="max-w-3xl mx-auto w-full px-6 pb-12 md:pb-16">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium mb-8 transition-colors group"
            >
              <span className="w-7 h-7 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <ArrowLeft size={14} />
              </span>
              Blog
            </Link>

            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6 drop-shadow-sm">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-brand-800 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white/20">
                  {post.author.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">{post.author}</p>
                  <p className="text-white/50 text-xs mt-0.5">{formatDate(post.created_at)}</p>
                </div>
              </div>
              <div className="hidden sm:block w-px h-6 bg-white/20" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Article ───────────────────────────────────────────────── */}
      <div className="max-w-3xl mx-auto px-6 py-14">

        {/* Excerpt callout */}
        {post.excerpt && (
          <div className="relative mb-10 pl-6">
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full bg-gradient-to-b from-brand-400 to-brand-700" />
            <p className="font-serif text-xl text-brand-800 font-medium leading-relaxed italic">
              {post.excerpt}
            </p>
          </div>
        )}

        {/* Body */}
        <div
          ref={contentRef}
          className="blog-prose"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* ── Photo gallery ─────────────────────────────────────── */}
        {extraImages.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center gap-4 mb-6">
              <h3 className="font-serif text-2xl font-bold text-brand-900">Foto's</h3>
              <div className="flex-1 h-px bg-brand-200/50" />
              <span className="text-xs text-stone-400">{extraImages.length} foto{extraImages.length !== 1 ? "'s" : ''}</span>
            </div>
            <div className={`grid gap-3 ${
              extraImages.length === 1 ? 'grid-cols-1' :
              extraImages.length === 2 ? 'grid-cols-2' :
              'grid-cols-2 md:grid-cols-3'
            }`}>
              {extraImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setLightboxIndex(idx + 1)}
                  className="group relative block overflow-hidden rounded-2xl border border-brand-100 shadow-sm hover:shadow-lg transition-all duration-300 aspect-video cursor-zoom-in"
                  aria-label={`Foto ${idx + 1} vergroten`}
                >
                  <img
                    src={img}
                    alt={`Foto ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-brand-900/35 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <ZoomIn size={16} className="text-white" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Author card ───────────────────────────────────────── */}
        <div className="mt-16 p-6 md:p-8 rounded-3xl bg-white border border-brand-100 shadow-sm flex items-start gap-5">
          <AuthorAvatar name={post.author} />
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-1">Geschreven door</p>
            <p className="font-serif text-lg font-bold text-brand-900">{post.author}</p>
            <p className="text-stone-400 text-xs mt-1 flex flex-wrap items-center gap-1.5">
              <Calendar size={11} className="text-brand-400" />
              Gepubliceerd op {formatDate(post.created_at)}
              {post.updated_at && post.updated_at !== post.created_at && (
                <> &nbsp;·&nbsp; bijgewerkt op {formatDate(post.updated_at)}</>
              )}
            </p>
          </div>
        </div>

        {/* ── Back to blog ──────────────────────────────────────── */}
        <div className="mt-10 flex items-center gap-4">
          <Link
            to="/blog"
            className="group inline-flex items-center gap-3 px-7 py-3.5 bg-brand-900 hover:bg-brand-800 text-white rounded-2xl text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
            Terug naar het blog
          </Link>
        </div>
      </div>

      {/* ── Lightbox ──────────────────────────────────────────────── */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[200] bg-black/92 backdrop-blur-sm flex items-center justify-center animate-fade-in"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Foto's bekijken"
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10 cursor-pointer"
            onClick={() => setLightboxIndex(null)}
            aria-label="Sluiten"
          >
            <X size={20} />
          </button>

          <button
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white disabled:opacity-20 transition-colors z-10 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => Math.max(0, (i ?? 0) - 1)); }}
            disabled={lightboxIndex === 0}
            aria-label="Vorige foto"
          >
            <ChevronLeft size={22} />
          </button>

          <div className="px-20 md:px-32 w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[lightboxIndex]}
              alt={`Foto ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain animate-scale-in"
            />
          </div>

          <button
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white disabled:opacity-20 transition-colors z-10 cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => Math.min(galleryImages.length - 1, (i ?? 0) + 1)); }}
            disabled={lightboxIndex === galleryImages.length - 1}
            aria-label="Volgende foto"
          >
            <ChevronRight size={22} />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white/70 text-sm font-medium tabular-nums">
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};
